import { sortBy } from 'lodash/fp';
import { DateTime } from 'luxon';
import { Route } from 'react-router-dom-v5-compat';

import Pagination from 'dashboard/components/Pagination';
import ReportDetails from 'dashboard/components/ReportDetails';
import ReportStatusSticker from 'dashboard/components/ReportStatusSticker';
import { SentryRoutes } from 'dashboard/components/SentryRoute/SentryRoute';
import { MonoSpan } from 'dashboard/components/Typography';
import TableLayout, {
  TableLinkTR,
  TableNullState,
  TableTD,
  TableTH,
} from 'dashboard/layouts/TableLayout';
import { SortParams } from 'dashboard/lib/interfaces/SortParams';
import { Report } from 'dashboard/models/report';
import ReportsTable, {
  ReportsTableColumns,
  reportTypeDisplay,
} from 'dashboard/views/DashboardReportsView/ReportsTable';
import Card from 'design-system/components/Card';
import { DATE_AND_TIME_FORMAT, TIMEZONE_NAME_ABBREVIATED } from 'lib/constants/luxonFormats';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import { PaginationParams } from 'lib/interfaces/api';

interface AccountReportsSectionProps {
  reports: Report[];
  paginationParams: PaginationParams;
  pageSize: number;
  handlePageLinkClick: (pageNumber: number) => void;
  handlePageSizeClick: (pageSize: number) => void;
  isLoading: boolean;
  isLoadingReports: boolean;
}

const AccountReportsSection = ({
  reports,
  paginationParams,
  pageSize,
  handlePageLinkClick,
  handlePageSizeClick,
  isLoading,
  isLoadingReports,
}: AccountReportsSectionProps) => {
  const paginationEnabled = useFeatureFlag('dashboard:accounts-paginate-account-objects');
  const reportRows = sortBy([(o) => DateTime.fromISO(o.attributes['created-at'])], reports)
    .reverse()
    .map((report) => <ReportTableRow key={report.id} report={report} />);

  return paginationEnabled ? (
    <Card>
      <Card.Body>
        <Card.Title>Reports</Card.Title>

        <TableLayout
          isEmpty={reports.length === 0}
          isLoading={isLoadingReports}
          showHeader={reports.length !== 0}
          headerTR={
            <>
              <TableTH>Report Type</TableTH>
              <TableTH>Report ID</TableTH>
              <TableTH>
                {`Created at (${DateTime.local().toFormat(TIMEZONE_NAME_ABBREVIATED)})`}
              </TableTH>
              <TableTH>Status</TableTH>
            </>
          }
          nullState={
            <TableNullState>
              <h3>Looks like there aren&#39;t any reports for this account yet.</h3>
            </TableNullState>
          }
        >
          {reportRows}
        </TableLayout>
        {reportRows.length > 0 && (
          <Pagination
            pageSize={pageSize}
            paginationParams={paginationParams}
            handlePageSizeClick={handlePageSizeClick}
            handlePageLinkClick={handlePageLinkClick}
          />
        )}
      </Card.Body>
    </Card>
  ) : (
    <SentryRoutes>
      <Route
        index
        element={
          <Card>
            <Card.Body>
              <ReportsTable
                reports={reports}
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                sortParams={{ sort: null, shouldSortDescending: true } as SortParams}
                isLoading={isLoading}
                hasUsedFilters={true}
                visibleColumns={[
                  ReportsTableColumns.PrimaryInput,
                  ReportsTableColumns.Status,
                  ReportsTableColumns.ReportId,
                  ReportsTableColumns.LastUpdatedAt,
                ]}
                handleSortUpdate={() => null}
              />
            </Card.Body>
          </Card>
        }
      />
      {reports.map((report) => (
        <Route
          key={report.id}
          path={report.id + '/*'}
          element={<ReportDetails report={report} multiCardFormat={false} />}
        />
      ))}
    </SentryRoutes>
  );
};

const ReportTableRow = ({ report }: { report: Report }) => {
  const reportType = report.type;
  return (
    <TableLinkTR to={`/dashboard/reports/${report.id}`}>
      <TableTD>{reportTypeDisplay(reportType)}</TableTD>
      <TableTD>
        <MonoSpan>{report.id}</MonoSpan>
      </TableTD>
      <TableTD>
        {DateTime.fromISO(report.attributes['created-at']).toFormat(DATE_AND_TIME_FORMAT)}
      </TableTD>
      <TableTD>
        {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
        <ReportStatusSticker report={report} />
      </TableTD>
    </TableLinkTR>
  );
};
export default AccountReportsSection;
